import { Field } from '../../Field/Field'
import { Spinner } from 'common/Spinner/Spinner'
import { ExternalIntegrationSelect } from 'components/ExternalIntegrationSelect/ExternalIntegrationSelect'
import { ExternalIntegrationItem } from 'components/ExternalIntegrationSelect/ExternalIntegrationItem'
import { integrationToName } from 'components/IntegrationRequiredModal/IntegrationRequiredModal'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'

import Styles from './ExternalIntegrationField.module.scss'
import { Button } from 'blaze-ui/Button'
import { LightningFilled } from 'blaze-ui/icons'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { useExternalIntegrationsRealtime } from './useExternalIntegrationsRealtime'
import { useField } from 'formik'
import { FieldSchema } from 'components/Wizards/Wizard/Form/Types'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { useEffect, useState, useCallback } from 'react'
import { generatePath } from 'react-router-dom'
import { BLAZE_HOME_PATH } from 'paths'
import useOpenWindowSafely from 'hooks/useOpenWindowSafely'
import { useAuth } from 'hooks/UseAuth'
import { isAlmanacEmail } from 'utils/featureFlagUserGroups'
import { useTestIntegrations } from 'hooks/queries/useTestIntegrations'
import { Text } from 'blaze-ui/Text/Text'
import { FormButton } from 'components/Wizards/Wizard/FormButton/FormButton'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

interface Props {
  schema: FieldSchema
}

export const ExternalIntegrationField = ({ schema }: Props) => {
  const [externalIntegrationIdField, , { setValue }] = useField<number | undefined>('external_integration_id')
  const setExternalIntegrationId = useCallback(
    (value: number) => {
      if (value === externalIntegrationIdField.value) {
        setValue(undefined)
      } else {
        setValue(value)
      }
    },
    [setValue, externalIntegrationIdField.value],
  )
  const { isMobile } = useResponsiveDesign()
  const { data, isLoading: externalIntegrationsAreLoading, refetch, isFetched } = useExternalIntegrations()
  const schemaMetadata = schema.describe().meta
  const integrationType = schemaMetadata.integrationType || ExternalIntegrationEnum.Instagram
  const supportedProviders = schemaMetadata.supportedProviders

  const auth = useAuth()
  const canSeeTestIntegrations = Boolean(auth?.email && isAlmanacEmail(auth?.email))
  const testIntegrationsQuery = useTestIntegrations({ enabled: canSeeTestIntegrations })
  const integrations = [
    ...(testIntegrationsQuery.data?.external_integrations || []),
    ...(data?.external_integrations || []),
  ]

  // Filter integrations based on supportedProviders (if available) or single integrationType
  const filteredIntegrations = integrations.filter((exInt) => {
    if (supportedProviders && Array.isArray(supportedProviders)) {
      return supportedProviders.includes(exInt.provider)
    }
    return exInt.provider === integrationType
  })
  const workspace = useCurrentWorkspace()
  const [hasSetExternalIntegrationId, sethasSetExternalIntegrationId] = useState(false)
  const openWindowSafely = useOpenWindowSafely()

  useExternalIntegrationsRealtime({
    workspaceId: workspace?.id,
    onMessage: () => refetch(),
  })

  useEffect(() => {
    if (!isFetched || hasSetExternalIntegrationId || externalIntegrationIdField.value) return

    if (filteredIntegrations?.length) {
      setExternalIntegrationId(filteredIntegrations[0].id)
    }

    sethasSetExternalIntegrationId(true)
  }, [
    isFetched,
    filteredIntegrations,
    setExternalIntegrationId,
    hasSetExternalIntegrationId,
    externalIntegrationIdField.value,
  ])

  const formatProviderNames = (providers: ExternalIntegrationEnum[]): string => {
    const validProviderNames = providers
      .map((provider) => integrationToName[provider])
      .filter((name) => name !== undefined)

    if (validProviderNames.length === 0) {
      return ''
    }

    if (validProviderNames.length === 1) {
      return validProviderNames[0]
    }

    if (validProviderNames.length === 2) {
      return `${validProviderNames[0]} or ${validProviderNames[1]}`
    }

    const lastProvider = validProviderNames[validProviderNames.length - 1]
    const otherProviders = validProviderNames.slice(0, -1)
    return `${otherProviders.join(', ')}, or ${lastProvider}`
  }

  // Generate the no integrations message based on supportedProviders
  const getNoIntegrationsMessage = (): string => {
    if (supportedProviders && Array.isArray(supportedProviders)) {
      const providerNames = formatProviderNames(supportedProviders)
      return providerNames
        ? `You don't have any ${providerNames} integrations connected at the moment. You can still generate your posts and then setup your integration.`
        : `You don't have any integrations connected at the moment. You can still generate your posts and then setup your integration.`
    }
    return `You don't have any ${integrationToName[integrationType]} integrations connected at the moment. You can still generate your posts and then setup your integration.`
  }

  const openIntegrationsInNewTab = () => {
    if (!workspace?.id) return

    const path = generatePath(BLAZE_HOME_PATH, { workspaceId: workspace.id })

    openWindowSafely(`${path}?modal=integrations`)
  }

  return (
    <Field label="Enable auto-posting by connecting your account">
      {externalIntegrationsAreLoading && (
        <div className={Styles.loader}>
          <Spinner />
        </div>
      )}
      {!externalIntegrationsAreLoading && filteredIntegrations?.length === 0 && workspace && (
        <div className={Styles.noIntegrationContainer}>
          <Text variant="secondary">{getNoIntegrationsMessage()}</Text>
          <div>
            <Button
              size="lg"
              variant="secondary"
              frontIcon={LightningFilled}
              onPress={() => {
                openIntegrationsInNewTab()
              }}
            >
              Connect Account
            </Button>
          </div>
        </div>
      )}
      {!externalIntegrationsAreLoading && (filteredIntegrations?.length || 0) > 0 && (
        <ExternalIntegrationSelect
          externalIntegrations={filteredIntegrations || []}
          selectedExternalIntegrationId={externalIntegrationIdField.value}
          onChange={setExternalIntegrationId}
          ButtonComponent={isMobile ? FormButton : undefined}
          variant="secondary"
          renderSelectValue={(item: any) => {
            if (!item) {
              return (
                <div className={Styles.defaultConnectButtonText}>
                  <LightningFilled />
                  <span>Connect Account</span>
                </div>
              )
            }

            const selectedExternalIntegration = filteredIntegrations?.find(
              (externalIntegration) =>
                externalIntegration.id.toString() === externalIntegrationIdField?.value?.toString(),
            )

            if (!selectedExternalIntegration) {
              return (
                <div className={Styles.defaultConnectButtonText}>
                  <LightningFilled />
                  <span>Connect Account</span>
                </div>
              )
            }

            return <ExternalIntegrationItem externalIntegration={selectedExternalIntegration} renderPlatformIcon />
          }}
        />
      )}
    </Field>
  )
}
